import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth, formatApiError } from "../AuthContext";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";

const PLANS = [
  {
    key: "monthly",
    name: "Monthly",
    price: "$9",
    cycle: "per month",
    features: [
      "Unlimited reframes",
      "Full private journal across devices",
      "Cancel any time",
    ],
  },
  {
    key: "yearly",
    name: "Yearly",
    price: "$79",
    cycle: "per year",
    badge: "Save $29",
    features: [
      "Unlimited reframes",
      "Full private journal across devices",
      "Two months on the house",
    ],
  },
];

export default function Upgrade() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => { refreshUser(); /* eslint-disable-next-line */ }, []);

  const handleCheckout = async (plan) => {
    setError(null);
    setLoading(plan);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        plan,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Could not start checkout. Try again.");
        setLoading(null);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Could not start checkout."));
      setLoading(null);
    }
  };

  const isActive = user?.subscription_status === "active";
  const expiresAt = user?.subscription_expires_at ? new Date(user.subscription_expires_at) : null;

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "Permanently delete your account and every reframe? This cannot be undone."
    );
    if (!confirmed) return;
    const typed = window.prompt("Type DELETE to confirm:");
    if (typed !== "DELETE") return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(`${API}/auth/account`);
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      setDeleteError(formatApiError(err.response?.data?.detail, "Could not delete account."));
      setDeleting(false);
    }
  };

  return (
    <div className="page" data-testid="upgrade-page">
      <div className="grain-bg" />
      <div className="container-wide">
        <AppHeader />

        <div className="fade-in">
          <div className="eyebrow" style={{ marginBottom: 16 }}>Membership</div>
          <h1 className="display" data-testid="upgrade-heading">
            {isActive ? "You're in." : "Unlimited reframes."}
          </h1>

          {isActive ? (
            <div data-testid="active-subscription-info" className="serif-italic" style={{ fontSize: 18, color: "var(--accent)", lineHeight: 1.7, marginBottom: 16 }}>
              You're on the <strong>{user.subscription_plan}</strong> plan
              {expiresAt && <> — active through <strong>{expiresAt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</strong></>}.
              <div style={{ marginTop: 28 }}>
                <button onClick={() => navigate("/app")} className="btn-underline" data-testid="active-back-to-app">
                  Back to today →
                </button>
              </div>
              <div className="divider" />
              <div className="eyebrow" style={{ marginBottom: 12 }}>Extend</div>
              <div style={{ fontSize: 16, color: "var(--accent)", marginBottom: 24 }}>
                Want to add more time? Pick a plan below — we'll stack it onto your current expiry.
              </div>
            </div>
          ) : (
            <p className="serif-italic" style={{ fontSize: 18, color: "var(--accent)", lineHeight: 1.7, maxWidth: 560, marginBottom: 16 }}>
              One free reframe a day is a start. The membership opens the floodgate — drop in every thought, every morning, every night.
            </p>
          )}

          {error && <div className="error-msg" data-testid="upgrade-error">{error}</div>}

          <div className="price-grid">
            {PLANS.map((p, idx) => (
              <div
                key={p.key}
                className={`price-card ${idx === 1 ? "featured" : ""}`}
                data-testid={`plan-card-${p.key}`}
              >
                {p.badge && <div className="ribbon" data-testid={`plan-badge-${p.key}`}>{p.badge}</div>}
                <div className="eyebrow" style={{ marginBottom: 4 }}>{p.name}</div>
                <div className="price-amount" data-testid={`plan-price-${p.key}`}>{p.price}</div>
                <div className="price-cycle">{p.cycle}</div>
                <div style={{ flex: 1, marginBottom: 24 }}>
                  {p.features.map((f, i) => (
                    <div key={i} className="price-feature">— {f}</div>
                  ))}
                </div>
                <button
                  className="btn-primary"
                  onClick={() => handleCheckout(p.key)}
                  disabled={loading !== null}
                  data-testid={`choose-plan-${p.key}`}
                >
                  {loading === p.key ? "Opening…" : (isActive ? "Add more time" : "Choose " + p.name)}
                </button>
              </div>
            ))}
          </div>

          <div
            className="serif-italic"
            style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.7, marginTop: 32, maxWidth: 560 }}
          >
            Payments are processed by Stripe. We never see your card.
            This is the test environment — use Stripe's test card 4242 4242 4242 4242, any future date, any CVC.
          </div>

          {/* Privacy & account deletion */}
          <div className="divider" style={{ marginTop: 48 }} />
          <div data-testid="account-controls" style={{ marginTop: 8 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Your data</div>
            <p
              className="serif-italic"
              style={{ fontSize: 16, color: "#1a1008", lineHeight: 1.7, marginBottom: 20, maxWidth: 560 }}
            >
              Your reframes are encrypted on our server and visible only to you. You can permanently
              delete your account and every entry at any time — this cannot be undone.
            </p>
            <button
              type="button"
              onClick={handleDeleteAccount}
              disabled={deleting}
              data-testid="delete-account-btn"
              className="btn-underline"
              style={{ color: "#8a3e2e" }}
            >
              {deleting ? "Deleting…" : "Delete account →"}
            </button>
            {deleteError && (
              <div className="error-msg" data-testid="delete-account-error" style={{ marginTop: 14 }}>
                {deleteError}
              </div>
            )}
          </div>

          <div className="footer-tag">Let Go. Live Free. Love Fully.</div>
        </div>
        <SiteFooter showTagline={false} />
      </div>
    </div>
  );
}
